"use client";

import { ExternalLink, CornerUpLeft, X, ChevronRight, ChevronLeft } from "lucide-react";
import { m, LazyMotion, AnimatePresence } from "motion/react";
import { useState } from "react";
import { useSwipeable } from "react-swipeable";
import Image from "components/Image";
import { variants } from "../utils/animationVariants";
import { range } from "../utils/range";
import type { ImageProps, SharedModalProps } from "../utils/types";

const loadFeatures = () => import("components/Tiles/utils/motionFeatures").then((res) => res.default);

export default function SharedModal({
  index,
  images = [],
  changePhotoId,
  closeModal,
  navigation,
  currentPhoto,
  direction,
  showAltLabel = false
}: SharedModalProps) {
  const [loaded, setLoaded] = useState(false);
  // @ts-ignore
  const filteredImages = images?.filter((img: ImageProps) => range(index - 15, index + 15).includes(img.id));

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (index < images?.length - 1) {
        changePhotoId(index + 1);
      }
    },
    onSwipedRight: () => {
      if (index > 0) {
        changePhotoId(index - 1);
      }
    },
    trackMouse: true
  });

  const currentImage = images.length ? images[index] : currentPhoto;

  return (
    <LazyMotion features={loadFeatures}>
      <div
        className="wide:h-full @7xl:taller-than-854:h-auto relative z-50 flex aspect-[3/2] max-h-[90%] w-full max-w-7xl items-center overflow-hidden"
        {...handlers}
      >
        {/* Main image */}
        <div className="w-full overflow-hidden">
          <div className="relative flex aspect-[3/2] items-center justify-center">
            <AnimatePresence initial={false}>
              <m.div
                key={index}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                className="absolute"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 }
                }}
              >
                <Image
                  src={currentImage?.src || ""}
                  width={navigation ? 1280 : 1920}
                  height={navigation ? 853 : 1280}
                  priority
                  alt={currentImage?.alt || "gallery image"}
                  onLoad={() => setLoaded(true)}
                />
                {!!(showAltLabel && currentImage?.alt) && (
                  <div
                    className={
                      "absolute bottom-1 right-1 max-w-[300px] select-none truncate rounded-full bg-neutral-dark-100 bg-opacity-60 px-2 py-1 text-sm font-medium text-white max-sm:max-w-[50%] max-sm:text-xs"
                    }
                  >
                    {currentImage?.alt}
                  </div>
                )}
              </m.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Buttons + bottom nav bar */}
        <div className="absolute inset-0 mx-auto flex max-w-7xl items-center justify-center">
          {/* Buttons */}
          {loaded && (
            <div className="relative aspect-[3/2] max-h-full w-full">
              {navigation && (
                <>
                  {index > 0 && (
                    <button
                      className="absolute left-3 top-[calc(50%-16px)] rounded-full bg-black/50 p-3 text-white/75 backdrop-blur-lg transition hover:bg-black/75 hover:text-white focus:outline-none"
                      style={{ transform: "translate3d(0, 0, 0)" }}
                      onClick={() => changePhotoId(index - 1)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                  )}
                  {index + 1 < images.length && (
                    <button
                      className="absolute right-3 top-[calc(50%-16px)] rounded-full bg-black/50 p-3 text-white/75 backdrop-blur-lg transition hover:bg-black/75 hover:text-white focus:outline-none"
                      style={{ transform: "translate3d(0, 0, 0)" }}
                      onClick={() => changePhotoId(index + 1)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  )}
                </>
              )}
              <div className="absolute right-0 top-0 flex items-center gap-2 p-3 text-white">
                {navigation ? (
                  <a
                    href={currentImage?.src}
                    className="hidden rounded-full bg-black/50 p-2 text-white/75 backdrop-blur-lg transition hover:bg-black/75 hover:text-white"
                    target="_blank"
                    title="Open fullsize version"
                    rel="noreferrer"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                ) : null}
              </div>
              <div className="absolute left-0 top-0 flex items-center gap-2 p-3 text-white">
                <button
                  onClick={closeModal}
                  className="rounded-full bg-black/50 p-2 text-white/75 backdrop-blur-lg transition hover:bg-black/75 hover:text-white"
                >
                  {navigation ? <X className="h-4 w-4" /> : <CornerUpLeft className="h-4 w-4" />}
                </button>
              </div>
            </div>
          )}
          {/* Bottom Nav bar */}
          {navigation && (
            <div className="bg-gradient-to-b fixed inset-x-0 bottom-0 z-40 overflow-hidden from-black/0 to-black/60">
              <m.div
                initial={false}
                className="mx-auto mb-6 mt-6 flex aspect-[3/2] h-14"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.2 }
                }}
              >
                <AnimatePresence initial={false}>
                  {filteredImages.map(({ id, src }) => (
                    <m.button
                      initial={{
                        width: "0%",
                        x: `${Math.max((index - 1) * -100, 15 * -100)}%`
                      }}
                      transition={{
                        x: { type: "spring", stiffness: 300, damping: 30 },
                        opacity: { duration: 0.2 }
                      }}
                      animate={{
                        scale: id === index ? 1.25 : 1,
                        width: "100%",
                        x: `${Math.max(index * -100, 15 * -100)}%`
                      }}
                      exit={{ width: "0%" }}
                      onClick={() => changePhotoId(id)}
                      key={id}
                      className={`${
                        id === index ? "z-20 rounded-md shadow shadow-black/50" : "z-10"
                      } ${id === 0 ? "rounded-l-md" : ""} ${
                        id === images.length - 1 ? "rounded-r-md" : ""
                      } relative inline-block w-full shrink-0 transform-gpu overflow-hidden focus:outline-none`}
                    >
                      <Image
                        alt="small photos on the bottom"
                        width={180}
                        height={120}
                        className={`${
                          id === index
                            ? "brightness-110 hover:brightness-110"
                            : "brightness-50 contrast-125 hover:brightness-75"
                        } h-full transform object-cover transition`}
                        src={src}
                      />
                    </m.button>
                  ))}
                </AnimatePresence>
              </m.div>
            </div>
          )}
        </div>
      </div>
    </LazyMotion>
  );
}
